import React from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const ClientListTable = ({
  clients,
  selectedClients,
  onSelectClient,
  onSelectAll,
  onViewClient,
  sortConfig,
  onSort
}) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-success/10', text: 'text-success', label: 'Active' },
      trial: { bg: 'bg-info/10', text: 'text-info', label: 'Trial' },
      suspended: { bg: 'bg-warning/10', text: 'text-warning', label: 'Suspended' },
      cancelled: { bg: 'bg-destructive/10', text: 'text-destructive', label: 'Cancelled' }
    };
    const config = statusConfig?.[status] || statusConfig?.active;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium font-caption ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const getActivityBadge = (level) => {
    const activityConfig = {
      high: { icon: 'TrendingUp', color: 'text-success' },
      medium: { icon: 'Minus', color: 'text-warning' },
      low: { icon: 'TrendingDown', color: 'text-destructive' }
    };
    const config = activityConfig?.[level] || activityConfig?.medium;
    return <Icon name={config?.icon} size={16} className={config?.color} />;
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    return sortConfig?.direction === 'asc' ? 
      <Icon name="ChevronUp" size={14} className="text-primary" /> : 
      <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  return (
    <div className="bg-card border border-border rounded-lg elevation-sm overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <Checkbox
                  checked={selectedClients?.length === clients?.length && clients?.length > 0}
                  onChange={onSelectAll}
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('companyName')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  Company
                  <SortIcon columnKey="companyName" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('contactName')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  Contact
                  <SortIcon columnKey="contactName" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('planType')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  Plan
                  <SortIcon columnKey="planType" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('mrr')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  MRR
                  <SortIcon columnKey="mrr" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('lastActivity')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                >
                  Last Activity
                  <SortIcon columnKey="lastActivity" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Activity</span>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {clients?.map((client) => (
              <tr key={client?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-3">
                  <Checkbox
                    checked={selectedClients?.includes(client?.id)}
                    onChange={() => onSelectClient(client?.id)}
                  />
                </td>
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-foreground font-body">{client?.companyName}</p>
                    <p className="text-xs text-muted-foreground font-caption">{client?.email}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm text-foreground font-body">{client?.contactName}</p>
                    <p className="text-xs text-muted-foreground font-caption">{client?.phone}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-foreground font-body">{client?.planType}</span>
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(client?.subscriptionStatus)}
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-semibold text-foreground font-body">₹{client?.mrr?.toLocaleString()}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground font-body">{client?.lastActivity}</span>
                </td>
                <td className="px-4 py-3">
                  {getActivityBadge(client?.activityLevel)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onViewClient(client)}
                      className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                      title="View Details"
                    >
                      <Icon name="Eye" size={16} className="text-muted-foreground" />
                    </button>
                    <button
                      className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                      title="Edit Client"
                    >
                      <Icon name="Edit" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {clients?.map((client) => (
          <div key={client?.id} className="p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <Checkbox
                  checked={selectedClients?.includes(client?.id)}
                  onChange={() => onSelectClient(client?.id)}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground font-body">{client?.companyName}</p>
                  <p className="text-xs text-muted-foreground font-caption mt-0.5">{client?.contactName}</p>
                </div>
              </div>
              {getActivityBadge(client?.activityLevel)}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground font-caption">
              <Icon name="Mail" size={12} />
              {client?.email}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground font-caption">
              <Icon name="Phone" size={12} />
              {client?.phone}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-foreground font-caption">{client?.planType}</span>
                {getStatusBadge(client?.subscriptionStatus)}
              </div>
              <span className="text-sm font-semibold text-foreground font-body">₹{client?.mrr?.toLocaleString()}/mo</span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="text-xs text-muted-foreground font-caption">Last active: {client?.lastActivity}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onViewClient(client)}
                  className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                >
                  <Icon name="Eye" size={16} className="text-muted-foreground" />
                </button>
                <button className="p-1.5 rounded-md hover:bg-muted transition-smooth">
                  <Icon name="Edit" size={16} className="text-muted-foreground" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {clients?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground font-caption">No clients found matching your filters</p>
        </div>
      )}
    </div>
  );
};

export default ClientListTable;